/**
 * HTTP + SSE client for ncoder-testing-service.
 *
 * Auth identical to services/scratch-org/index.ts (canonical pattern via
 * useAuthStoreV1 + getAuthToken).
 */
import axios from "axios";
import env from "@/config/env";
import { getAuthToken } from "@/dataProvider/axios";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { getDefaultOrgId } from "@/utils/defaultOrg";
import {
	planExecuteEndpoint,
	planHealProposalAcceptEndpoint,
	planHealProposalRejectEndpoint,
	planVerifyEndpoint,
	runHealEndpoint,
	runReportAccessEndpoint,
} from "./queries";
import type { TestingChatEvent } from "./types";

const BASE_URL =
	env.TESTING_URL || `${env.API_URL.replace(/\/+$/, "")}/proxy/testing`;

// Plain axios — deliberately NO 401-triggers-logout interceptor.
// A failed testing-service call (e.g. bearer not yet hydrated on first paint)
// must never log the user out of shell-web. Each call surfaces its own error
// to the canvas; the canvas decides whether to retry / hide / warn.
export const testingAxios = axios.create({
	baseURL: BASE_URL,
	headers: { "Content-Type": "application/json" },
	// Planning/execute calls can run multiple minutes. Match the main
	// axios instance (120s) so the UI doesn't bail before the server replies.
	timeout: 120000,
	withCredentials: false,
});

/**
 * Resolve a path returned by the testing-service (e.g. `/api/runs/<id>/report/index.html`,
 * a screenshot `.png`, or a `.webm` video) to an absolute URL suitable for iframe / <img> /
 * <video> / window.open. Returns null if no path.
 *
 * These assets are loaded by the BROWSER directly, which cannot attach the bearer + identity
 * headers our API proxy (`/proxy/testing`) requires — so a proxied URL returns 401 Unauthorized
 * ("clicking the report shows unauthorized"). The testing-service serves report assets WITHOUT
 * auth, so we route them through DIRECT_TESTING_URL, which bypasses the bearer proxy (the same
 * mechanism the live view uses). Falls back to the proxy base only when no direct URL is set.
 */
export function testingAbsoluteUrl(
	path: string | null | undefined,
): string | null {
	if (!path) return null;
	if (/^https?:\/\//i.test(path)) return path;
	const base = (env.DIRECT_TESTING_URL || BASE_URL).replace(/\/$/, "");
	return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

// ─── Typed errors ────────────────────────────────────────────────────────
//
// The testing-service tells the user WHAT TO DO in the error body (e.g. a 409 "this plan has no
// validated IR — re-plan it in chat"). Throwing a bare `Error("409 Conflict")` — which the SSE
// openers used to do — discards exactly the sentence the UI needs to surface. Every failed open now
// reads the body and surfaces it here. (There is no approval flow: /execute validates the plan's
// stored contract at run time; a rejection is information to surface, not a decision to make.)
//
// Two body flavours exist server-side and both are handled:
//   • `c.json({ error: "…" })` / `{ message: "…" }` — application/json
//   • `throw new HTTPException(status, { message })` — a bare text/plain string
export class TestingApiError extends Error {
	readonly status: number;
	readonly code?: string;
	/** Parsed JSON body when the server sent one — lets callers read extra fields without re-parsing. */
	readonly body?: unknown;

	constructor(
		status: number,
		message: string,
		opts?: { code?: string; body?: unknown },
	) {
		super(message);
		this.name = "TestingApiError";
		this.status = status;
		this.code = opts?.code;
		this.body = opts?.body;
	}
}

export function isTestingApiError(e: unknown): e is TestingApiError {
	return e instanceof TestingApiError;
}

/** Pull the server's own words out of a failed `fetch` Response (JSON `{error|message}` or bare text). */
async function testingErrorFromResponse(res: Response): Promise<TestingApiError> {
	let text = "";
	try {
		text = await res.text();
	} catch {
		/* body already consumed / network died — fall back to the status line */
	}
	const trimmed = text.trim();
	let message = "";
	let code: string | undefined;
	let body: unknown;

	if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
		try {
			body = JSON.parse(trimmed);
			const o = body as Record<string, unknown> | null;
			const raw = o?.error ?? o?.message;
			if (typeof raw === "string") message = raw;
			else if (raw && typeof raw === "object") {
				const nested = (raw as Record<string, unknown>).message;
				if (typeof nested === "string") message = nested;
				const nestedCode = (raw as Record<string, unknown>).code;
				if (typeof nestedCode === "string") code = nestedCode;
			}
			if (!code && typeof o?.code === "string") code = o.code;
		} catch {
			/* not actually JSON — treat the raw text as the message below */
		}
	}
	if (!message) message = trimmed;
	if (!message) message = `${res.status} ${res.statusText}`.trim();
	return new TestingApiError(res.status, message, { code, body });
}

/** Same normalisation for the axios paths, so callers only ever branch on `TestingApiError`. */
export function testingErrorFromAxios(err: unknown): TestingApiError {
	const e = err as {
		response?: { status?: number; statusText?: string; data?: unknown };
		message?: string;
	};
	const status = e?.response?.status ?? 0;
	const data = e?.response?.data;
	let message = "";
	let code: string | undefined;
	if (typeof data === "string") message = data.trim();
	else if (data && typeof data === "object") {
		const o = data as Record<string, unknown>;
		const raw = o.error ?? o.message;
		if (typeof raw === "string") message = raw;
		else if (raw && typeof raw === "object") {
			const nested = (raw as Record<string, unknown>).message;
			if (typeof nested === "string") message = nested;
		}
		if (typeof o.code === "string") code = o.code;
	}
	if (!message) message = e?.message || `${status || "request"} failed`;
	return new TestingApiError(status, message, { code, body: data });
}

export function testingAuthHeaders(
	orgId?: string | null,
): Record<string, string> {
	const state = useAuthStoreV1.getState();
	const effectiveOrgId =
		orgId ||
		state.selectedOrg?.id ||
		(state.user as unknown as { organization_id?: string } | undefined)
			?.organization_id ||
		getDefaultOrgId() ||
		readBrowserValue("org_id") ||
		null;
	const headers: Record<string, string> = {};
	if (effectiveOrgId) {
		headers["X-Organization-ID"] = String(effectiveOrgId);
		// headers["X-Org-ID"] = String(effectiveOrgId);
	}
	const effectiveUserId = state.user?.id || readBrowserValue("user_id");
	if (effectiveUserId) headers["X-User-ID"] = String(effectiveUserId);
	const token =
		getAuthToken() || readBrowserValue("auth_token") || localDevBearer();
	if (token) headers.Authorization = `Bearer ${token}`;
	return headers;
}

// NOTE: the old noVNC live-view helpers (getLiveTicket / liveViewUrl) were removed along with the
// backend's VNC/websockify routes. The live browser view is the authenticated SCREENSHOT-FRAME
// stream (the `screenshot` SSE events rendered by ExecutionFeed) — not VNC.

export function testingAuthReady(): boolean {
	const state = useAuthStoreV1.getState();
	const orgId =
		state.selectedOrg?.id ||
		(state.user as unknown as { organization_id?: string } | undefined)
			?.organization_id ||
		getDefaultOrgId() ||
		readBrowserValue("org_id");
	const token =
		getAuthToken() || readBrowserValue("auth_token") || localDevBearer();
	const userId = state.user?.id || readBrowserValue("user_id");
	return Boolean(token && userId && orgId);
}

function readBrowserValue(name: string): string | null {
	if (typeof window === "undefined") return null;
	try {
		const stored = window.localStorage.getItem(name);
		if (stored) return stored;
		const cookie = document.cookie
			.split(";")
			.map((part) => part.trim())
			.find((part) => part.startsWith(`${name}=`));
		return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
	} catch {
		return null;
	}
}

function localDevBearer(): string | null {
	if (typeof window === "undefined") return null;
	const host = window.location.hostname;
	if (host !== "localhost" && host !== "127.0.0.1") return null;
	return "local-testing-dev-bearer-token";
}

// ─── Endpoints kept here (used by SSE hook / approve flow) ──────────────

/** Verdict from POST /api/plans/:id/verify (pre-flight best-of-N headless run). */
export interface VerifyResult {
	verified: boolean;
	passed: number;
	total: number;
	flaky: boolean;
	attempts: Array<{
		run_id: string;
		status: string;
		passed: number;
		failed: number;
		error: string | null;
	}>;
	triage: Record<string, unknown> | null;
	stopped?: "passed" | "deterministic" | null;
	error?: string;
}

/**
 * Pre-flight VERIFY: run the plan headless against the live org BEFORE the user runs it, so they
 * only run a confirmed-working scenario. Returns the verdict. Long-running (a real browser run +
 * retries), so this call gets an extended timeout.
 */
export async function verifyPlan(args: {
	planId: string;
	attempts?: number;
}): Promise<VerifyResult> {
	const r = await testingAxios.post<VerifyResult>(
		planVerifyEndpoint(args.planId),
		{ attempts: args.attempts },
		{ headers: testingAuthHeaders(), timeout: 360000 },
	);
	return r.data;
}

// ─── Self-heal PROPOSAL (accept / reject) ────────────────────────────────
//
// A heal stream no longer mutates the source run's verdict — it PROPOSES a patch set and stops.
// The user then accepts (patches are applied to the plan, optionally verified) or rejects it.
//
// Every ANSWERED outcome leaves no pending proposal server-side:
//   200 → applied (or discarded, for reject)
//   409 → the proposal no longer applies (plan lost its IR / patches don't validate / the heal
//         would weaken the test) — and the server DELETES it as part of answering
//   404 → nothing was pending to begin with
// So all three mean "drop your local proposal and refetch the plan", which is why 404/409 come back
// as normal results (`cleared: true`) rather than throwing. Only transport/auth/5xx errors throw.
//
// These endpoints are the odd ones out in the service's error vocabulary: they answer 404/409 with
// JSON `{ applied, message }` where the rest of the API uses `{ error }` or bare text/plain.

export interface HealProposalAcceptResult {
	applied: boolean;
	message?: string;
	patches?: import("./types").HealPatch[];
	/** The run that executed the PATCHED plan and passed. Present only when `applied`. */
	verification_run_id?: string;
	/** Freshly re-read plan row. Present only when `applied`. */
	plan?: import("./types").TestPlan;
	/** True once the server answered at all — no proposal remains stored. */
	cleared: boolean;
	/** 200 applied · 409 stale-but-cleared · 404 nothing was pending. */
	status: number;
}

export interface HealProposalRejectResult {
	applied: false;
	cleared: true;
	message?: string;
	status: number;
}

/** Apply a pending heal proposal to the plan. Verifies, then rewrites the IR + appends a revision. */
export async function acceptHealProposal(
	planId: string,
): Promise<HealProposalAcceptResult> {
	try {
		const r = await testingAxios.post<Omit<HealProposalAcceptResult, "cleared" | "status">>(
			planHealProposalAcceptEndpoint(planId),
			{},
			{ headers: testingAuthHeaders(), timeout: 360000 },
		);
		return { ...r.data, cleared: true, status: r.status };
	} catch (err) {
		const e = testingErrorFromAxios(err);
		if (e.status === 409 || e.status === 404) {
			return { applied: false, cleared: true, message: e.message, status: e.status };
		}
		throw e;
	}
}

/** Discard a pending heal proposal without applying it. */
export async function rejectHealProposal(
	planId: string,
): Promise<HealProposalRejectResult> {
	try {
		const r = await testingAxios.post<{ applied: false; cleared: true; message?: string }>(
			planHealProposalRejectEndpoint(planId),
			{},
			{ headers: testingAuthHeaders() },
		);
		return { ...r.data, applied: false, cleared: true, status: r.status };
	} catch (err) {
		const e = testingErrorFromAxios(err);
		if (e.status === 409 || e.status === 404) {
			return { applied: false, cleared: true, message: e.message, status: e.status };
		}
		throw e;
	}
}

// ─── Run report access ───────────────────────────────────────────────────

/** Short-lived signed URL for a run's HTML report, from POST /api/runs/:runId/report-access. */
export interface RunReportAccess {
	url: string;
	expires_at: string;
}

/**
 * Mint a browser-openable report URL. Report assets are fetched by the BROWSER, which cannot attach
 * the bearer + identity headers our `/proxy/testing` route requires — so the server hands back a
 * pre-authorised, expiring URL instead. Every report link must go through here rather than
 * hand-building a path off `summary.report_url`.
 */
export async function mintRunReportAccess(runId: string): Promise<RunReportAccess> {
	try {
		const r = await testingAxios.post<RunReportAccess>(
			runReportAccessEndpoint(runId),
			{},
			{ headers: testingAuthHeaders() },
		);
		return r.data;
	} catch (err) {
		throw testingErrorFromAxios(err);
	}
}

/**
 * Mint + open a run's report in a new tab. Returns the absolute URL that was opened (resolved
 * against DIRECT_TESTING_URL, falling back to the proxy base), or null when nothing could be opened.
 */
export async function openRunReport(runId: string): Promise<string | null> {
	const { url } = await mintRunReportAccess(runId);
	const absolute = testingAbsoluteUrl(url);
	if (absolute && typeof window !== "undefined") {
		window.open(absolute, "_blank", "noopener,noreferrer");
	}
	return absolute;
}

/**
 * "What to test next" for a finished run: the LLM's follow-up test suggestions, grounded in the
 * objects this run touched (their untested fields / validation rules / actions). Best-effort —
 * returns an empty list when the org can't be read or there's nothing to suggest, so the outcome
 * card simply shows no chips rather than an error. GET /api/runs/:runId/suggestions
 */
export async function fetchRunSuggestions(
	runId: string,
): Promise<import("./types").RunSuggestion[]> {
	const r = await testingAxios.get<{ suggestions: import("./types").RunSuggestion[] }>(
		`/api/runs/${runId}/suggestions`,
		{ headers: testingAuthHeaders(), timeout: 60000 },
	);
	return r.data.suggestions ?? [];
}

// ─── Regression suites (/api/suites) ──────────────────────────────────────

export async function fetchSuites(): Promise<import("./types").TestSuite[]> {
	const r = await testingAxios.get<{ suites: import("./types").TestSuite[] }>("/api/suites", {
		headers: testingAuthHeaders(),
	});
	return r.data.suites;
}

export async function fetchSuite(
	id: string,
): Promise<{ suite: import("./types").TestSuite; runs: import("./types").TestSuiteRun[] }> {
	const r = await testingAxios.get<{
		suite: import("./types").TestSuite;
		runs: import("./types").TestSuiteRun[];
	}>(`/api/suites/${id}`, { headers: testingAuthHeaders() });
	return r.data;
}

export async function createSuite(args: {
	name: string;
	description?: string | null;
	plan_ids: string[];
}): Promise<import("./types").TestSuite> {
	const r = await testingAxios.post<{ suite: import("./types").TestSuite }>("/api/suites", args, {
		headers: testingAuthHeaders(),
	});
	return r.data.suite;
}

export async function updateSuite(
	id: string,
	patch: { name?: string; description?: string | null; plan_ids?: string[] },
): Promise<import("./types").TestSuite> {
	const r = await testingAxios.patch<{ suite: import("./types").TestSuite }>(
		`/api/suites/${id}`,
		patch,
		{ headers: testingAuthHeaders() },
	);
	return r.data.suite;
}

export async function deleteSuite(id: string): Promise<void> {
	await testingAxios.delete(`/api/suites/${id}`, { headers: testingAuthHeaders() });
}

export async function runSuite(
	id: string,
	opts?: { label?: string; concurrency?: number },
): Promise<{ suite_run_id: string; total: number }> {
	const r = await testingAxios.post<{ suite_run_id: string; total: number }>(
		`/api/suites/${id}/run`,
		opts ?? {},
		{ headers: testingAuthHeaders() },
	);
	return r.data;
}

export async function fetchSuiteRun(runId: string): Promise<import("./types").TestSuiteRun> {
	const r = await testingAxios.get<{ run: import("./types").TestSuiteRun }>(
		`/api/suites/runs/${runId}`,
		{ headers: testingAuthHeaders() },
	);
	return r.data.run;
}

/**
 * The CI/CD gate report for a finished suite run: JUnit XML plus the gate verdict the backend
 * computes (exit code + reason), the same one `pnpm ci:gate` uses. `policy=regression` fails only on
 * scenarios that regressed vs the prior baseline run; `failOnBlocked` also counts blocked as a fail.
 */
export async function fetchSuiteRunJunit(
	runId: string,
	opts?: { policy?: "any-red" | "regression"; failOnBlocked?: boolean },
): Promise<{ xml: string; exitCode: number; reason: string }> {
	const r = await testingAxios.get<string>(`/api/suites/runs/${runId}/junit`, {
		params: {
			policy: opts?.policy,
			fail_on_blocked: opts?.failOnBlocked ? 1 : undefined,
		},
		headers: testingAuthHeaders(),
		responseType: "text",
		// Force text — the default transform would try to JSON.parse the XML body.
		transformResponse: [(d) => d],
	});
	return {
		xml: r.data,
		exitCode: Number(r.headers["x-gate-exit-code"] ?? 2),
		reason: String(r.headers["x-gate-reason"] ?? ""),
	};
}

/** Trigger a browser download of a JUnit XML string as `junit-<runId>.xml`. */
export function downloadJunitReport(runId: string, xml: string): void {
	if (typeof window === "undefined") return;
	const blob = new Blob([xml], { type: "application/xml" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `junit-${runId}.xml`;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}

export async function fetchCoverageDashboard(): Promise<import("./types").CoverageDashboard> {
	const r = await testingAxios.get<import("./types").CoverageDashboard>(
		"/api/plans/coverage/dashboard",
		{ headers: testingAuthHeaders(), timeout: 60000 },
	);
	return r.data;
}

export async function fetchSuiteDiff(id: string): Promise<{
	diff: import("./types").SuiteDiff | null;
	latest: import("./types").TestSuiteRun | null;
	prior: import("./types").TestSuiteRun | null;
}> {
	const r = await testingAxios.get<{
		diff: import("./types").SuiteDiff | null;
		latest: import("./types").TestSuiteRun | null;
		prior: import("./types").TestSuiteRun | null;
	}>(`/api/suites/${id}/diff`, { headers: testingAuthHeaders() });
	return r.data;
}

// ─── SSE chat ───────────────────────────────────────────────────────────

// The chat SSE subscription streams through the SAME authenticated proxy
// (BASE_URL) as the execute/heal streams — one deterministic path, no direct-URL
// override. The stream carries both the proxy's bearer auth AND the
// server-verified ticket, so it's authenticated at both layers.
const SSE_BASE_URL = BASE_URL.replace(/\/+$/, "");

/** Resolve after `ms`, or immediately if the signal aborts first. Never rejects —
 *  the caller re-checks `signal.aborted` after awaiting. Used to pace the chat SSE
 *  reconnect backoff without hanging past a user cancel. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
	return new Promise((resolve) => {
		if (signal.aborted) {
			resolve();
			return;
		}
		const onAbort = () => {
			clearTimeout(timer);
			resolve();
		};
		const timer = setTimeout(() => {
			signal.removeEventListener("abort", onAbort);
			resolve();
		}, ms);
		signal.addEventListener("abort", onAbort, { once: true });
	});
}

interface MessageAcceptedResponse {
	accepted: boolean;
	session_id: string;
	event_stream_url: string;
	sse_ticket: string;
}

/**
 * Send a chat message and stream the agent's events.
 *
 * Two-step, matching the hardened backend pipeline:
 *   1. POST /api/chat/messages (authenticated command) → 202 with a session id,
 *      a stream URL, and a short-lived HMAC `sse_ticket`. The run now executes
 *      server-side independently of this call.
 *   2. GET the ticket-authenticated SSE subscription and stream events until
 *      `run_end` (or abort). Because the run is decoupled, closing this stream no
 *      longer cuts the turn short — it keeps running and persists to the DB.
 *
 * If the subscription drops WITHOUT a terminal event (network blip, proxy reset),
 * it reconnects with a small capped backoff, passing `last_event_id` so the server
 * replays only the events missed since — the same pattern the dev-agent SSE client
 * (`pages/dev-agent/sse`) uses. Ids are deduped across the reconnect boundary.
 *
 * The onEvent/onError/onClose contract is unchanged, so callers don't change.
 */
export function streamChatMessage(args: {
	content: string;
	sessionId?: string | null;
	/** Plan the user is actively tweaking, so the agent edits the right plan. */
	activePlanId?: string | null;
	onEvent: (e: TestingChatEvent) => void;
	onError?: (err: Error) => void;
	onClose?: () => void;
}): () => void {
	const ctrl = new AbortController();
	// Idempotency key so a retried POST doesn't start a second run; stable per-tab
	// client id so a reconnect replaces its prior stream instead of stacking.
	const clientMessageId = crypto.randomUUID();
	const clientId = crypto.randomUUID();

	void (async () => {
		try {
			// ── 1. Command ──
			const postRes = await fetch(`${BASE_URL}/api/chat/messages`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...testingAuthHeaders(),
				},
				body: JSON.stringify({
					content: args.content,
					session_id: args.sessionId || null,
					active_plan_id: args.activePlanId || null,
					client_message_id: clientMessageId,
				}),
				signal: ctrl.signal,
			});
			// Read the body BEFORE giving up — the server's message is the whole payload here.
			if (!postRes.ok) throw await testingErrorFromResponse(postRes);
			const accepted = (await postRes.json()) as MessageAcceptedResponse;

			// Preserve the legacy first-event contract: callers derive the session
			// id from a `session` event.
			args.onEvent({
				type: "session",
				session_id: accepted.session_id,
			} as TestingChatEvent);

			// ── 2. Subscription (bounded reconnect-on-drop) ──
			// Build the base URL by string concat (not new URL) so a relative proxy
			// BASE_URL still resolves — fetch handles relative URLs, new URL doesn't.
			// event_stream_url already carries `?session_id=…`, so append with `&`.
			// ticket + client_id are stable for the whole turn; last_event_id is added
			// per-attempt so a reconnect replays only what we missed.
			const sep = accepted.event_stream_url.includes("?") ? "&" : "?";
			const baseStreamUrl =
				`${SSE_BASE_URL}${accepted.event_stream_url}${sep}` +
				`ticket=${encodeURIComponent(accepted.sse_ticket)}` +
				`&client_id=${encodeURIComponent(clientId)}`;

			// Dedup by SSE id: an event can arrive both via replay and live in the
			// subscribe→replay race window (and again across a reconnect boundary) —
			// the backend re-delivers rather than risk dropping. Ids are monotonic per
			// session, so we also track the max seen to replay from it on reconnect.
			const seenIds = new Set<string>();
			let lastEventId = "0";

			// Read one subscription to its natural end. Returns:
			//   "terminal"     — saw run_end/timeout (the turn is done)
			//   "unauthorized" — ticket rejected mid-stream; stop and surface via close
			//   "dropped"      — the stream closed with no terminal event (network
			//                    blip); the run keeps executing server-side, so the
			//                    caller reconnects and replays from last_event_id.
			// `tolerateOpenError` lets reconnect attempts treat a failed re-open as a
			// drop (keep retrying) while the FIRST open still surfaces HTTP errors.
			const readOnce = async (
				tolerateOpenError: boolean,
			): Promise<"terminal" | "unauthorized" | "dropped"> => {
				const streamUrl = `${baseStreamUrl}&last_event_id=${encodeURIComponent(lastEventId)}`;
				let sseRes: Response;
				try {
					sseRes = await fetch(streamUrl, {
						method: "GET",
						// Two auth layers: the proxy's bearer identity (headers) and the
						// server-verified ticket (query). Browsers can't set headers on
						// EventSource, so the ticket rides the query string.
						headers: { Accept: "text/event-stream", ...testingAuthHeaders() },
						signal: ctrl.signal,
					});
				} catch (err) {
					if ((err as Error).name === "AbortError") throw err;
					if (tolerateOpenError) return "dropped";
					throw err;
				}
				if (!sseRes.ok) {
					if (tolerateOpenError) return "dropped";
					throw await testingErrorFromResponse(sseRes);
				}
				if (!sseRes.body) {
					if (tolerateOpenError) return "dropped";
					throw new TestingApiError(sseRes.status, "chat stream returned no body");
				}

				const reader = sseRes.body.getReader();
				const decoder = new TextDecoder();
				let buf = "";
				let terminal: "terminal" | "unauthorized" | null = null;
				try {
					while (!terminal) {
						const { done: streamDone, value } = await reader.read();
						if (streamDone) break;
						buf += decoder.decode(value, { stream: true });
						let idx: number;
						while ((idx = buf.indexOf("\n\n")) >= 0) {
							const raw = buf.slice(0, idx);
							buf = buf.slice(idx + 2);
							const { event, id } = parseFrame(raw, args.onEvent, seenIds);
							// Advance the replay cursor to the highest id we've processed.
							if (id) {
								const n = Number.parseInt(id, 10);
								const cur = Number.parseInt(lastEventId, 10);
								if (!Number.isNaN(n) && (Number.isNaN(cur) || n > cur)) lastEventId = id;
							}
							// Bound the dedup set so a long run can't grow it unboundedly
							// (mirrors the dev-agent SSE client's cap).
							if (seenIds.size > 500) {
								const recent = [...seenIds].slice(-300);
								seenIds.clear();
								for (const s of recent) seenIds.add(s);
							}
							// run_end/timeout end the turn; unauthorized means the ticket
							// was rejected — stop reconnecting. Control frames
							// (connected/heartbeat) are forwarded but don't end it.
							if (event === "run_end" || event === "timeout") {
								terminal = "terminal";
								break;
							}
							if (event === "unauthorized") {
								terminal = "unauthorized";
								break;
							}
						}
					}
				} catch (err) {
					try {
						await reader.cancel();
					} catch {
						// already closed
					}
					if ((err as Error).name === "AbortError") throw err;
					// A mid-stream read error is a network drop, not a run failure.
					return "dropped";
				}
				try {
					await reader.cancel();
				} catch {
					// already closed
				}
				return terminal ?? "dropped";
			};

			// Open the stream, then reconnect-with-replay on any non-terminal drop,
			// with a small capped exponential backoff (~1s → 8s). The run is decoupled
			// from this stream, so a dropped connection never cuts the turn short.
			const MAX_RECONNECTS = 5;
			let outcome = await readOnce(false);
			for (let attempt = 0; outcome === "dropped" && attempt < MAX_RECONNECTS; attempt++) {
				if (ctrl.signal.aborted) return;
				await sleep(Math.min(1000 * 2 ** attempt, 8000), ctrl.signal);
				if (ctrl.signal.aborted) return;
				outcome = await readOnce(true);
			}

			ctrl.abort();
			args.onClose?.();
		} catch (err) {
			if ((err as Error).name === "AbortError") return;
			args.onError?.(err as Error);
		}
	})();

	return () => ctrl.abort();
}

/**
 * Best-effort stop for a decoupled chat run. Aborting the local SSE stream no
 * longer ends the server-side turn (the run is decoupled from the stream), so an
 * explicit user cancel must ALSO tell the backend to stop the run — otherwise it
 * keeps executing and billing after the user hit stop. Errors are swallowed: the
 * local stream is already being torn down, and a failed stop must never surface as
 * a chat error (the run may simply have already ended). Mirrors the dev-agent
 * client's `stopDevAgentExecution` (POST /api/chat/sessions/:id/stop).
 */
export async function stopChatSession(sessionId: string): Promise<void> {
	if (!sessionId) return;
	try {
		await testingAxios.post(
			`/api/chat/sessions/${encodeURIComponent(sessionId)}/stop`,
			undefined,
			{ headers: testingAuthHeaders() },
		);
	} catch {
		// best-effort — the run may already have ended, or the network dropped.
	}
}

/** Parse the raw text of one SSE frame into its `event:` name, `id:`, and
 *  `data:` lines. The single source of truth for SSE wire parsing, shared by the
 *  chat (two-step, deduped) reader and the execute/heal (single-POST) reader. */
function parseSseFrame(frame: string): { event: string; id: string; data: string[] } {
	let event = "message";
	let id = "";
	const data: string[] = [];
	for (const line of frame.split("\n")) {
		if (line.startsWith("event:")) event = line.slice(6).trim();
		else if (line.startsWith("id:")) id = line.slice(3).trim();
		else if (line.startsWith("data:")) data.push(line.slice(5).trim());
	}
	return { event, id, data };
}

/** Parse one chat SSE frame, forward it to onEvent (unless its id was already
 *  seen), and return its event name for terminal-event detection. */
function parseFrame(
	frame: string,
	onEvent: (e: TestingChatEvent) => void,
	seenIds?: Set<string>,
): { event: string; id: string } {
	const { event, id, data } = parseSseFrame(frame);
	// Return the event name + id even for dedup'd/dataless frames so run_end is
	// still detected as terminal, and the replay cursor still advances, on a replay.
	if (id && seenIds) {
		if (seenIds.has(id)) return { event, id };
		seenIds.add(id);
	}
	if (!data.length) return { event, id };
	try {
		onEvent({ type: event, ...JSON.parse(data.join("\n")) } as TestingChatEvent);
	} catch {
		// skip malformed
	}
	return { event, id };
}

// ─── SSE execute + heal ──────────────────────────────────────────────────

function streamSse<T>(
	url: string,
	body: object,
	args: {
		onEvent: (e: T) => void;
		onError?: (err: Error) => void;
		onClose?: () => void;
	},
): () => void {
	const ctrl = new AbortController();

	// Stall watchdog: a run keeps executing server-side even if the live SSE goes silent (proxy drop,
	// network blip, a wedged connection). Without this a hung stream would spin forever with no
	// feedback. If no bytes arrive for STALL_MS we abort the read and fall through to onClose — the
	// same graceful end as a normal close — so the caller reconnects via the polled run history
	// (the "run in progress on the server — reconnecting…" path) instead of showing a dead spinner.
	const STALL_MS = 120_000;
	let stalled = false;
	let watchdog: ReturnType<typeof setTimeout> | undefined;
	const armWatchdog = () => {
		if (watchdog) clearTimeout(watchdog);
		watchdog = setTimeout(() => {
			stalled = true;
			ctrl.abort();
		}, STALL_MS);
	};

	void (async () => {
		try {
			armWatchdog();
			const res = await fetch(url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...testingAuthHeaders(),
				},
				body: JSON.stringify(body),
				signal: ctrl.signal,
			});
			// A pre-stream rejection (409 no-validated-IR / 409 raw-spec disabled / 410 heal
			// feature disabled) arrives as a normal HTTP error whose body carries the actionable
			// sentence. Surface it as a TestingApiError so callers can branch on `.status`.
			if (!res.ok) throw await testingErrorFromResponse(res);
			if (!res.body)
				throw new TestingApiError(res.status, "stream returned no body");
			const reader = res.body.getReader();
			const decoder = new TextDecoder();
			let buf = "";
			while (true) {
				const { done, value } = await reader.read();
				if (done) break;
				armWatchdog(); // reset the stall timer on every chunk of server activity
				buf += decoder.decode(value, { stream: true });
				let idx: number;
				while ((idx = buf.indexOf("\n\n")) >= 0) {
					const frame = buf.slice(0, idx);
					buf = buf.slice(idx + 2);
					const { event, data } = parseSseFrame(frame);
					if (!data.length) continue;
					try {
						args.onEvent({ type: event, ...JSON.parse(data.join("\n")) } as T);
					} catch {
						/* skip malformed */
					}
				}
			}
			args.onClose?.();
		} catch (err) {
			// A stall aborts the read the same way a manual cancel does; only the stall should end
			// gracefully via onClose (manual cancel is a deliberate stop — see the returned closer).
			if ((err as Error).name === "AbortError") {
				if (stalled) args.onClose?.();
				return;
			}
			args.onError?.(err as Error);
		} finally {
			if (watchdog) clearTimeout(watchdog);
		}
	})();

	return () => {
		stalled = false; // explicit user cancel — not a stall, so no onClose
		ctrl.abort();
	};
}

/**
 * Open the execute stream. Rejections BEFORE the stream opens (409 no-validated-IR, 409 raw-spec
 * disabled) arrive through `onError` as a `TestingApiError` carrying the server's own instruction —
 * branch on `.status === 409` and render `.message`.
 *
 * NOTE: `headed` / `live` are sent for backwards compatibility but the server no longer reads a
 * request body on this route; the live view is the authenticated screenshot-frame stream.
 */
export function streamExecutePlan(args: {
	planId: string;
	headed?: boolean;
	/** Legacy; ignored server-side. */
	live?: boolean;
	onEvent: (e: import("./types").ExecuteStreamEvent) => void;
	onError?: (err: Error) => void;
	onClose?: () => void;
}): () => void {
	return streamSse(
		`${BASE_URL}${planExecuteEndpoint(args.planId)}`,
		{ headed: args.headed ?? false, live: args.live ?? false },
		{ onEvent: args.onEvent, onError: args.onError, onClose: args.onClose },
	);
}

/**
 * Open the heal stream. This PROPOSES an IR patch set — it never re-runs the plan and never changes
 * the source run's verdict. Terminates with a `complete` carrying `status: 'proposed' | 'declined'`
 * (see `isHealComplete`), NOT an ExecuteResult.
 *
 * `onError` receives a `TestingApiError` with status 410 when the feature is disabled server-side,
 * or 409 when the plan has no validated IR to patch.
 */
export function streamHealRun(args: {
	runId: string;
	onEvent: (e: import("./types").HealStreamEvent) => void;
	onError?: (err: Error) => void;
	onClose?: () => void;
}): () => void {
	return streamSse(
		`${BASE_URL}${runHealEndpoint(args.runId)}`,
		{},
		{ onEvent: args.onEvent, onError: args.onError, onClose: args.onClose },
	);
}

export * from "./queries";
export * from "./types";
